<template>
  <div
    class="promo-code rounded-m bg-primary-primary shadow-card-large"
    :data-state="state"
    data-testid="promo-code"
  >
    <div class="tint tint-almostthere" />
    <div class="tint tint-active" />

    <div aria-hidden="true" class="illustration">
      <LcImage v-bind="illustration!" alt="" v-if="illustration" />
    </div>

    <LcExpandable
      v-model="isExpanded"
      action-class="promo-code-action"
      animated
      class="promo-code-expandable"
      content-class="promo-code-content"
      :disabled="!!resultVariant"
      header-class="promo-code-header"
      hide-icon
      @expand="trackExpand"
      @expanded="focusInput"
    >
      <template #header>
        <span
          ref="header"
          class="header-content flex flex-1 items-center min-w-000"
          data-testid="promo-code-header-content"
          @transitionend="releaseHeight"
        >
          <EditorPromoCodeResult
            v-bind="{ coupon: coupon! }"
            :key="resultVariant"
            :chip="$t(`editor.promocode.chip.${resultVariant}`)"
            :class="{ 'fade-in': fadesInContent }"
            :info="resultInfo"
            :variant="resultVariant!"
            v-if="resultVariant"
          />

          <span
            :class="['flex flex-1 justify-between items-center', { 'fade-in': fadesInContent }]"
            v-else
          >
            <span class="headline-h5-headline" data-testid="promo-code-headline">
              {{ $t('bonuscodeinput.headline') }}
            </span>

            <span
              aria-hidden="true"
              class="plus flex flex-none justify-center items-center w-300 icon-primary-primary"
            >
              <LcIcon name="plus" />
            </span>
          </span>
        </span>
      </template>

      <template #action v-if="resultVariant">
        <button
          :aria-label="$t('bonuscodeinput.arialabel.delete')"
          :class="[
            'trash rounded-m flex flex-none justify-center items-center w-300 h-300 bg-primary-primary icon-primary-primary',
            { 'fade-in': fadesInContent },
          ]"
          data-testid="promo-code-remove"
          type="button"
          @click="remove"
        >
          <LcIcon name="trash-full" size="small" />
        </button>
      </template>

      <LcForm
        v-bind="{ initialValues, validationSchema }"
        ref="form"
        v-slot="{ meta }"
        class="flex flex-col gap-150 pt-none px-200 pb-150"
        :tracking="{ containerId: 'bonus_code_input_form' }"
        @submit="submit"
      >
        <LcFormField v-slot="{ field }" name="bonusCode">
          <LcInput
            v-bind="field"
            ref="input"
            data-testid="promo-code-input"
            :error-message="errorMessage ?? field.errorMessage"
            :label="$t('bonuscodeinput.label')"
            no-feedback
            @update:value="clearError"
          />
        </LcFormField>

        <p
          class="caption-regular flex items-center gap-050 m-none text-accent-danger-danger"
          data-testid="promo-code-error"
          v-if="errorMessage"
        >
          <LcIcon name="alert-triangle" size="small" />
          <span>{{ errorMessage }}</span>
        </p>

        <LcButton
          class="confirm"
          :disabled="!meta.dirty || !meta.valid"
          :loading="loading"
          type="submit"
          variant="secondary"
        >
          {{ $t('bonuscodeinput.button') }}
        </LcButton>
      </LcForm>
    </LcExpandable>
  </div>
</template>

<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers'

import { LcInput } from '@lottocom/frontend-components'
import * as Yup from 'yup'

import type { BonusCodeValidationInput } from '#api/shared/@types/generated/backend/graphql-schema-types'

import LcForm from '#tracking/app/component-overrides/LcForm.vue'
import LcFormField from '#tracking/app/component-overrides/LcFormField.vue'

import type { TrackingEventType } from '../../../../../app/utils/tracking'

import {
  EDITOR_PROMO_CODE_MOTION,
  EDITOR_PROMO_CODE_STATE,
  type EditorPromoCodeResultVariant,
  type EditorPromoCodeState,
} from '../constants/promoCode'

type FormValues = Pick<BonusCodeValidationInput, 'bonusCode'>

type FormRef = ComponentExposed<typeof LcForm<FormValues>>

type Props = {
  conditionMessage: Maybe<string>
  coupon: Maybe<BonusCodeCoupon>
  errorMessage: Maybe<string>
  loading: boolean
  verdict: Maybe<BonusCodeVerdict>
}

const { conditionMessage, coupon, errorMessage, verdict } = defineProps<Props>()

const emit = defineEmits<{
  apply: [bonusCode: string]
  clearError: []
  remove: []
}>()

const { getIllustration } = useAsset()
const prefersReducedMotion = useReducedMotion()

const {
  pushCheckoutRemovePromoCodeEvent,
  pushCheckoutStartAddPromoCodeEvent,
  pushCheckoutSubmitPromoCodeEvent,
} = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const formRef = useTemplateRef<FormRef>('form')
const headerRef = useTemplateRef<HTMLElement>('header')
const inputRef = useTemplateRef<ComponentExposed<typeof LcInput>>('input')

const isExpanded = ref(false)
const fadesInContent = ref(false)

const initialValues: FormValues = { bonusCode: '' }

const illustration = computed(() => getIllustration('gift-cutted'))

const { bonusCode: bonusCodeRule } = useNuxtApp().$yup as { bonusCode: Yup.StringSchema }

const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object({
  bonusCode: bonusCodeRule.required(),
})

const resultVariant = computed<Maybe<EditorPromoCodeResultVariant>>(() => {
  if (!coupon) return null
  if (verdict === BonusCodeVerdict.Fulfilled) return EDITOR_PROMO_CODE_STATE.Active
  if (verdict === BonusCodeVerdict.Fulfillable) return EDITOR_PROMO_CODE_STATE.AlmostThere
  return null
})

const resultInfo = computed(() => {
  if (resultVariant.value === EDITOR_PROMO_CODE_STATE.Active) {
    return coupon?.info.description ?? ''
  }

  return conditionMessage ?? ''
})

const state = computed<EditorPromoCodeState>(() => {
  if (resultVariant.value) return resultVariant.value
  if (errorMessage) return EDITOR_PROMO_CODE_STATE.Error
  return isExpanded.value ? EDITOR_PROMO_CODE_STATE.Open : EDITOR_PROMO_CODE_STATE.Closed
})

const clearError = () => {
  if (errorMessage) emit('clearError')
}

const focusInput = () => {
  inputRef.value?.$el.querySelector('input')?.focus()
}

const remove = () => {
  track('checkout_remove_promo_code', pushCheckoutRemovePromoCodeEvent)
  emit('remove')
  collapse()
}

const collapse = () => {
  isExpanded.value = false
  formRef.value?.internal?.resetForm()
}

const submit = ({ bonusCode }: FormValues) => {
  track('checkout_submit_promo_code', pushCheckoutSubmitPromoCodeEvent)
  emit('apply', bonusCode)
}

const trackExpand = () => {
  track('checkout_start_add_promo_code', pushCheckoutStartAddPromoCodeEvent)
}

const track = (topic: TrackingEventType, push: () => void) => {
  try {
    push()
  } catch (err) {
    logTrackingError(topic, err)
  }
}

const animateHeight = async () => {
  const element = headerRef.value
  if (!element || prefersReducedMotion.value) return

  const from = element.offsetHeight
  fadesInContent.value = true

  await nextTick()

  element.style.height = ''
  const target = element.offsetHeight
  if (from === target) return

  element.style.transition = 'none'
  element.style.height = `${from}px`
  void element.offsetHeight
  element.style.transition = ''
  element.style.height = `${target}px`
}

const releaseHeight = (event: TransitionEvent) => {
  const element = headerRef.value
  if (!element || event.propertyName !== 'height' || event.target !== element) return

  element.style.height = ''
}

watch(resultVariant, animateHeight)

// the code can disappear without the customer, e.g. on expiry
watch(
  () => !!coupon,
  (hasCoupon, hadCoupon) => {
    if (hadCoupon && !hasCoupon) collapse()
  },
)

watch(
  () => verdict,
  (newVerdict) => {
    if (!newVerdict) return
    isExpanded.value = false
  },
)

watch(
  () => errorMessage,
  (message) => {
    if (!message) return
    isExpanded.value = true
  },
  { immediate: true },
)

// an import referenced only in style v-bind() is not exposed to the ssr render, a local binding is
const promoCodeMotion = EDITOR_PROMO_CODE_MOTION

defineExpose({ collapse })
</script>

<style lang="scss" scoped>
$ease: cubic-bezier(0.22, 1, 0.36, 1);

.promo-code {
  position: relative;
  overflow: hidden;
  scroll-margin: token('size/space/gap/300');
  border: 1px dashed transparent;
  transition: border-color v-bind('promoCodeMotion.tint') ease;
}

.tint {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity v-bind('promoCodeMotion.tint') ease;
}

.tint-almostthere {
  background: linear-gradient(
    90deg,
    token('color/background/primary/primary') 0%,
    token('color/background/accent/warning/warning') 100%
  );
}

.tint-active {
  background: linear-gradient(
    90deg,
    token('color/background/primary/primary') 0%,
    token('color/green/100') 100%
  );
}

.illustration {
  position: absolute;
  top: 0;
  right: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity v-bind('promoCodeMotion.tint') ease;
}

.promo-code[data-state='almostthere'] {
  border-color: token('color/border/accent/warning/warning');

  .tint-almostthere {
    opacity: 1;
  }
}

.promo-code[data-state='active'] {
  border-color: token('color/border/accent/success/success');

  .tint-active {
    opacity: 1;
  }

  .illustration {
    opacity: 1;
  }
}

.promo-code :deep(.promo-code-header) {
  height: auto;
  min-height: 1.5rem;
  padding: token('size/space/gap/150') token('size/space/gap/200');
  text-align: left;
}

.promo-code :deep(.promo-code-content) {
  padding-top: 0;
}

.promo-code :deep(.promo-code-expandable) {
  position: relative;
}

.promo-code :deep(.promo-code-action) {
  padding-right: token('size/space/gap/200');
}

.header-content {
  overflow: hidden;
  transition: height $transition-duration-md $transition-ease-default;
}

.fade-in {
  animation: promo-code-fade-in $transition-duration-md $transition-ease-default both;
}

@keyframes promo-code-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.trash {
  border: 0;
  box-shadow:
    0 6px 10px rgb(0, 0, 0, 10%),
    0 -1px 4px rgb(0, 0, 0, 10%);
  cursor: pointer;
}

.plus {
  flex: 0 0 1.5rem;
  transition: transform v-bind('promoCodeMotion.rotate') $ease;
}

.promo-code[data-state='open'] .plus,
.promo-code[data-state='error'] .plus {
  transform: rotate(45deg);
}

.confirm {
  align-self: center;
  max-width: 16rem;
}

@media (prefers-reduced-motion: reduce) {
  .promo-code,
  .tint,
  .illustration,
  .plus,
  .header-content {
    transition: none;
  }

  .fade-in {
    animation: none;
  }
}
</style>
